import { UserPreferenceContext } from '@contexts/user-preference-context';
import { Feature } from '@enums/feature.enum';
import { UseBaseHooks } from '@hooks/use-base.hooks';
import logoIconDark from '@images/logo-icon-dark.svg';
import testSigmaLogoDark from '@images/logo-testsigma-dark.svg';
import AiAttoIcon from '@images/ai-atto-colored.svg';
import { KeyCode } from '@modules/command-center/components/tour';
import { SalesforceConnectionsService } from '@modules/salesforce/services';
import MenuItem from '@navigation/menu-item';
import { useGlobalState } from '@providers/global-context-provider';
import { Icon, useInternalEvents, useTranslation } from '@testsigma/atoms';
import classNames from 'classnames';
import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import HelpNavMenu from '../help-nav-menu';
import ProjectSwitcher from '../project-switcher';
import { RefreshMetadataMenu } from '../refresh-metadata-menu';
import UserProfile from '../user-profile';
import TestDataMenu from './test-data-menu';
import TestWritingMenu from './test-writing-menu';
import PageLoading from '@shared/components/loading';
import { AdditionalMenu } from './additional-settings';
import { useSFConnectionState } from '@contexts/sf-connection-context';
import { ApplicationType } from '@testsigmainc/constants';

const ProjectSettingsModal = React.lazy(
  () => import('@modules/project/settings/index'),
);

interface LeftIconProps {
  isPrimaryMenuExpanded?: boolean;
  classNameValue?: string;
  isSidebarhovered: boolean;
  setIsSidebarhovered: (flag: boolean) => void;
  setIsModelOpen: (flag: boolean) => void;
}

export type PrimaryMenuItemType = {
  lIcon: string;
  displayText: string;
  url: string;
  urlMatcherRegex: RegExp;
  isBorder: boolean;
  isChild: any;
  getMissingFeature?: boolean;
  featureName?: Feature;
  displayLink?: boolean;
  canShow?: boolean;
  iconCustomClass?: string;
  textClassName?: string;
  isIconImage?: boolean;
};

const PrimaryMenu: React.FC<LeftIconProps> = ({
  isPrimaryMenuExpanded,
  classNameValue,
  isSidebarhovered,
  setIsSidebarhovered,
  setIsModelOpen,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [disableSideNav, setDisableSideNav] = useState<boolean>(false);
  const [showProjectSettings, toggleProjectSettings] = useState<boolean>(false);

  const {
    metadataConnection: salesforceMetadataConnection,
    hasMetadataConnection,
    handleMetadataConnection: setSalesforceMetadataConnection,
  } = useSFConnectionState();

  const { userPreference } = useContext(UserPreferenceContext);
  const { reloadPrimaryMenu } = useGlobalState();

  const { hasFeature } = UseBaseHooks();
  const isSalesforce =
    userPreference?.preference?.projectPreference?.selectedVersion?.application
      ?.isSalesforce;
  const projectId = userPreference?.preference?.projectPreference?.projectId;
  const isWindowsApplication =
    userPreference?.preference?.projectPreference?.selectedApplication
      ?.applicationType === ApplicationType.WINDOWS;
  const isRestAPIApplication =
    userPreference?.preference?.projectPreference?.selectedApplication
      ?.applicationType === ApplicationType.Rest;

  const showSFConnections = useMemo(
    () => hasFeature(Feature.SALESFORCE_AUTOMATION) && isSalesforce,
    [hasFeature, isSalesforce],
  );

  useEffect(() => {
    setIsModelOpen(showProjectSettings);
    if (!showProjectSettings) setIsSidebarhovered(false);
  }, [showProjectSettings]);

  const primaryMenuItems: PrimaryMenuItemType[] = [
    {
      lIcon: 'speed',
      displayText: 'left.nav.dashboard',
      url: 'dashboard',
      urlMatcherRegex: new RegExp(/dashboard/g),
      isBorder: false,
      isChild: null,
      displayLink: true,
      canShow: true,
    },
    {
      lIcon: isPrimaryMenuExpanded ? AiAttoIcon : 'ai-atto-agent',
      displayText: 'common.atto.home',
      url: 'ai-journey',
      urlMatcherRegex: new RegExp(/ai-journey/g),
      displayLink: false,
      isBorder: false,
      isChild: undefined,
      isIconImage: isPrimaryMenuExpanded,
      canShow:
        (hasFeature(Feature.AGENTIC_AI) || hasFeature(Feature.GEN_AI)) &&
        !isWindowsApplication &&
        !isRestAPIApplication,
    },
    {
      lIcon: 'test-development',
      displayText: 'left.nav.create_test',
      url: 'td/cases',
      urlMatcherRegex: new RegExp(
        /(td\/(\d+)\/(requirements|functions|elements|step-groups|cases))|(td\/(requirements|functions|elements|step_groups|cases)\/+.*)/g,
      ),
      isBorder: false,
      displayLink: false,
      isChild: <TestWritingMenu showChildrenWithText={isPrimaryMenuExpanded} />,
      canShow: true,
    },
    {
      lIcon: 'test-data',
      displayText: 'left.nav.test_data',
      url: 'td/data',
      urlMatcherRegex: new RegExp(
        /(td\/(\d+)\/(data|environments|uploads))|(td\/(data|environments|uploads)\/(\d+)+.*)/g,
      ),
      isBorder: false,
      displayLink: false,
      isChild: <TestDataMenu showChildrenWithText={isPrimaryMenuExpanded} />,
      canShow: true,
    },
    {
      lIcon: 'suites',
      displayText: 'left.nav.test_suite',
      url: 'td/suites',
      urlMatcherRegex: new RegExp(/td\/(\d+)\/suites|td\/suites\/(\d+)+.*/g),
      isBorder: false,
      displayLink: true,
      isChild: null,
      canShow: true,
    },
    {
      lIcon: 'plans',
      displayText: 'left.nav.test_plan',
      url: 'td/plans',
      urlMatcherRegex: new RegExp(/td\/(\d+)\/plans|td\/plans\/(\d+)+.*/g),
      isBorder: false,
      displayLink: true,
      isChild: null,
      canShow: true,
    },
    {
      lIcon: 'results',
      displayText: 'left.nav.run_result',
      url: 'td/results',
      urlMatcherRegex: new RegExp(/td\/((\d+)\/)?(results|runs)/g),
      isBorder: false,
      displayLink: true,
      isChild: null,
      canShow: true,
    },
    {
      lIcon: 'settings',
      displayText: 'left.nav.settings',
      url: 'settings',
      urlMatcherRegex: new RegExp(/(settings)/g),
      isBorder: false,
      displayLink: true,
      isChild: null,
      canShow: true,
    },
    {
      lIcon: 'backup',
      displayText: 'left.nav.save_points',
      url: 'save_points',
      urlMatcherRegex: new RegExp(/(save_points)/g),
      isBorder: false,
      displayLink: true,
      isChild: null,
      canShow: hasFeature(Feature.IMPORT_ARTEFACTS),
    },
  ];

  useEffect(() => {
    const applicationVersionId =
      userPreference?.preference?.projectPreference?.selectedVersionId;
    const applicationId =
      userPreference?.preference?.projectPreference?.selectedApplicationId;
    const isSalesforceApp =
      userPreference?.preference?.projectPreference?.selectedVersion
        ?.application?.isSalesforce;
    // if the application type is Salesforce and there is no connection disable the sidenav
    if (applicationVersionId && isSalesforceApp) {
      SalesforceConnectionsService.getVersionSpecificConnection(
        applicationVersionId,
        applicationId,
      ).then((res) => {
        if (res?.content?.length) {
          const apiSalesforceMetadataConnection = res?.content?.[0];
          setSalesforceMetadataConnection(apiSalesforceMetadataConnection);

          setDisableSideNav(false);
        } else {
          navigate(`/salesforce/${projectId}/connection`);
          setDisableSideNav(true);
        }
      });
      // if the application type is other than Salesforce enable the sidenav
    } else if (!isSalesforce) {
      setDisableSideNav(false);
    }
  }, [
    userPreference?.preference?.projectPreference?.selectedVersionId,
    isSalesforce,
    reloadPrimaryMenu,
  ]);

  useEffect(() => {
    if (hasMetadataConnection && salesforceMetadataConnection) {
      setDisableSideNav(false);
    }
  }, [hasMetadataConnection, salesforceMetadataConnection]);

  const globalEventHandler = useCallback(
    (event) => {
      if (event == 'open-project-settings') toggleProjectSettings(true);
    },
    [toggleProjectSettings],
  );

  useInternalEvents(['open-project-settings'], globalEventHandler);

  const isMac = navigator.userAgent.indexOf('Mac') > -1;

  return (
    <>
      <div
        className={classNames(
          classNameValue,
          {
            'top-0 left-0 !w-[220px] h-[100vh] shadow-2xl fixed z-[2000] bg-secondary-100':
              isSidebarhovered,
          },
          {
            'relative h-full': !isSidebarhovered,
          },
        )}
      >
        <div
          className={classNames('flex items-center  h-16 cursor-pointer', {
            'pl-4': isPrimaryMenuExpanded || isSidebarhovered,
            'justify-center': !isPrimaryMenuExpanded,
          })}
          onClick={() => !disableSideNav && navigate('/dashboard')}
        >
          <img
            src={isPrimaryMenuExpanded ? testSigmaLogoDark : logoIconDark}
            className={classNames({
              'w-8 h-8': !isPrimaryMenuExpanded,
              'h-8': isPrimaryMenuExpanded,
            })}
            alt="Testsigma logo"
          />
        </div>
        <ProjectSwitcher
          isPrimaryMenuExpanded={isPrimaryMenuExpanded}
          setIsSidebarhovered={setIsSidebarhovered}
          toggleProjectSettings={toggleProjectSettings}
          setIsModelOpen={setIsModelOpen}
        />
        <div
          className={classNames(
            isPrimaryMenuExpanded ? 'px-lg' : 'items-center justify-center',
            { 'pointer-events-none opacity-50': disableSideNav },
            'flex flex-col basis-full px-lg transition-all ease-in duration-500 overflow-y-auto overflow-x-hidden pb-4',
          )}
          style={{
            maxHeight: isPrimaryMenuExpanded
              ? showSFConnections
                ? 'calc(100% - 350px)'
                : 'calc(100% - 290px)'
              : '',
          }}
        >
          {primaryMenuItems.map(
            (item: PrimaryMenuItemType) =>
              item?.canShow && (
                <div
                  className={classNames('flex flex-wrap mb-0.5', {
                    'basis-full': isPrimaryMenuExpanded,
                  })}
                  key={item?.displayText}
                >
                  <MenuItem
                    isLeftIcon={item?.lIcon}
                    itemKey={item?.displayText}
                    navUrl={item?.url}
                    showWithText={isPrimaryMenuExpanded}
                    urlMatcherRegex={item?.urlMatcherRegex}
                    displayLink={item.displayLink}
                    iconCustomClass={item.iconCustomClass}
                    textClassName={item.textClassName}
                    isIconImage={item.isIconImage}
                  >
                    {item.isChild}
                  </MenuItem>
                </div>
              ),
          )}

          {showSFConnections && (
            <div
              className={classNames('flex flex-wrap mb-0.5', {
                'basis-full': isPrimaryMenuExpanded,
              })}
            >
              <MenuItem
                isLeftIcon={'salesforce'}
                itemKey={'left.nav.salesforce_connections'}
                navUrl={`/salesforce/${projectId}/connection`}
                showWithText={isPrimaryMenuExpanded}
                urlMatcherRegex={new RegExp(/salesforce\/(\d+)\/connection/g)}
                isChild={null}
              ></MenuItem>
            </div>
          )}
        </div>

        <div
          className={classNames(
            isPrimaryMenuExpanded ? '' : 'justify-center',
            'flex flex-wrap basis-full overflow-hidden px-lg w-full',
          )}
          style={{
            position: 'absolute',
            bottom: '20px',
          }}
        >
          <div
            className={classNames([
              'text-sm font-medium gap-1.5 flex items-center mb-4 mt-2 px-1',
              'ts-cs-textshine',
            ])}
          >
            {isPrimaryMenuExpanded && (
              <>
                <KeyCode className='!w-5 !h-5'>
                  {isMac ? (
                    '⌘'
                  ) : (
                    <Icon
                      name='expand-more'
                      className='-rotate-180 text-lg'
                      toolTip='Ctrl'
                    />
                  )}
                </KeyCode>
                <KeyCode className='!w-5 !h-5'>K</KeyCode>
                <span>{t('command.center.navmenu')}</span>
              </>
            )}
          </div>
          {showSFConnections && salesforceMetadataConnection && (
            <div
              className={classNames('flex flex-wrap mb-4 w-full', {
                'basis-full': isPrimaryMenuExpanded,
              })}
            >
              <RefreshMetadataMenu
                isPrimaryMenuExpanded={isPrimaryMenuExpanded}
                connectionId={salesforceMetadataConnection?.id}
              />
            </div>
          )}

          <AdditionalMenu
            disableMenu={disableSideNav}
            setIsSidebarhovered={setIsSidebarhovered}
            setIsModelOpen={setIsModelOpen}
            show={isPrimaryMenuExpanded}
          />

          {!isPrimaryMenuExpanded && (
            <HelpNavMenu
              isDisabled={disableSideNav}
              setIsModelopen={setDisableSideNav}
            />
          )}
          <div className='my-[10px] border-[0.5px] border-secondary-100 basis-full mt-5' />
          <UserProfile
            showChildrenWithText={isPrimaryMenuExpanded}
            setIsSidebarhovered={setIsSidebarhovered}
            isSidebarhovered={isSidebarhovered}
          />
        </div>
      </div>

      <Suspense fallback={<PageLoading />}>
        <ProjectSettingsModal
          showProjectSettings={showProjectSettings}
          toggleProjectSettings={toggleProjectSettings}
        />
      </Suspense>
    </>
  );
};

export default PrimaryMenu;
